import React from 'react'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Paper from '@mui/material/Paper'

function EntityDetailsDrawerSkeleton() {
  return (
    <>
      <Box
        sx={{
          marginLeft: 2,
          marginRight: 2,
        }}
      >
        {/* Name/Company Display (Typography h6) */}
        <Skeleton variant="text" width="60%" height={32} />

        {/* Address Display with LaunchIcon */}
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Skeleton variant="text" width="50%" height={22} />
          <Skeleton variant="rectangular" width={20} height={20} />
        </Stack>

        {/* ActionButtons - Notes and Attachments IconButtons */}
        <Box
          sx={{
            display: 'flex',
            gap: 2.3,
            mt: 2.4,
          }}
        >
          <Skeleton variant="circular" width={48} height={48} />
          <Skeleton variant="circular" width={48} height={48} />
        </Box>

        {/* Main Buttons Stack */}
        <Stack display="flex" gap={1.2}>
          {/* Service / Agreement Button */}
          <Skeleton
            variant="rectangular"
            width="100%"
            height={42}
            sx={{ mt: 2.4, borderRadius: 1 }}
          />
          {/* Knock Status Button */}
          <Skeleton
            variant="rectangular"
            width="100%"
            height={42}
            sx={{ borderRadius: 1 }}
          />
          {/* CardCapture + Scheduling Row */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
            }}
          >
            <Skeleton
              variant="rectangular"
              width="50%"
              height={42}
              sx={{ borderRadius: 1 }}
            />
            <Skeleton
              variant="rectangular"
              width="50%"
              height={42}
              sx={{ borderRadius: 1 }}
            />
          </Box>
        </Stack>

        {/* EntityInformation - Paper with outlined variant */}
        <Paper
          variant="outlined"
          sx={{
            mt: 1.6,
            px: 1.2,
            pt: 1.2,
            pb: 0.7,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* NextActionDate */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Skeleton variant="text" width="40%" height={24} />
            <Skeleton variant="circular" width={24} height={24} />
          </Stack>
          {/* EntityTypeDisplay */}
          <Skeleton variant="text" width="30%" height={28} sx={{ mb: 0.8 }} />
          {/* Status */}
          <Stack direction="row" alignItems="center" gap={0.8}>
            <Skeleton variant="text" width={50} height={20} />
            <Skeleton
              variant="rectangular"
              width={80}
              height={24}
              sx={{ borderRadius: 2 }}
            />
          </Stack>
          {/* Knock Status */}
          <Stack direction="row" alignItems="center" gap={0.8} sx={{ mt: 0.8 }}>
            <Skeleton variant="text" width={80} height={20} />
            <Skeleton
              variant="rectangular"
              width={70}
              height={24}
              sx={{ borderRadius: 2 }}
            />
          </Stack>
        </Paper>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mx: 1.2, mt: 0.4 }}>
        <Stack direction="row" spacing={2} sx={{ px: 1 }}>
          <Skeleton variant="text" width={70} height={48} />
          <Skeleton variant="text" width={110} height={48} />
        </Stack>
      </Box>

      {/* Content Area - Overview Tab (EntityLocationDetails) */}
      <Box mt={2}>
        <Stack
          direction="column"
          sx={{
            paddingLeft: 2,
            paddingRight: 2,
          }}
          spacing={0}
          mt={0}
        >
          {/* BasicInfo Section */}
          <Stack>
            {/* Phone */}
            <Stack direction="row" alignItems="center">
              <Skeleton variant="text" width={50} height={22} />
              <Skeleton
                variant="text"
                width={120}
                height={32}
                sx={{ ml: 0.5 }}
              />
            </Stack>
            {/* Email */}
            <Stack direction="row" alignItems="center">
              <Skeleton variant="text" width={45} height={22} />
              <Skeleton
                variant="text"
                width={150}
                height={32}
                sx={{ ml: 0.5 }}
              />
            </Stack>
            {/* LocationLink */}
            <Stack direction="row" alignItems="center">
              <Skeleton variant="text" width={55} height={22} />
              <Skeleton
                variant="text"
                width={100}
                height={32}
                sx={{ ml: 0.5 }}
              />
            </Stack>
          </Stack>

          {/* ExtraDetails Section */}
          <Stack gap={0.4} mt={1.2}>
            {/* Age */}
            <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-start' }}>
              <Skeleton variant="text" width={35} height={22} />
              <Skeleton variant="text" width={60} height={22} />
            </Box>
            {/* Alt. Phone */}
            <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-start' }}>
              <Skeleton variant="text" width={70} height={22} />
              <Skeleton variant="text" width={100} height={22} />
            </Box>
            {/* Date Created */}
            <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-start' }}>
              <Skeleton variant="text" width={90} height={22} />
              <Skeleton variant="text" width={80} height={22} />
            </Box>
            {/* Created By */}
            <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-start' }}>
              <Skeleton variant="text" width={75} height={22} />
              <Skeleton variant="text" width={100} height={22} />
            </Box>
            {/* Services */}
            <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-start' }}>
              <Skeleton variant="text" width={60} height={22} />
              <Skeleton variant="text" width={120} height={22} />
            </Box>
            {/* Owner */}
            <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-start' }}>
              <Skeleton variant="text" width={50} height={22} />
              <Skeleton variant="text" width={90} height={22} />
            </Box>
            {/* Sales Funnel */}
            <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-start' }}>
              <Skeleton variant="text" width={85} height={22} />
              <Skeleton variant="text" width={100} height={22} />
            </Box>
            {/* Sales Team */}
            <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-start' }}>
              <Skeleton variant="text" width={75} height={22} />
              <Skeleton variant="text" width={80} height={22} />
            </Box>
            {/* Total Value */}
            <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-start' }}>
              <Skeleton variant="text" width={80} height={22} />
              <Skeleton variant="text" width={60} height={22} />
            </Box>
          </Stack>

          {/* View detail page Button */}
          <Skeleton
            variant="rectangular"
            width={140}
            height={32}
            sx={{ mt: 1.2, borderRadius: 1 }}
          />
        </Stack>
      </Box>
    </>
  )
}

export default EntityDetailsDrawerSkeleton
